import { useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import api, { API_BASE_URL } from "../../utils/api";
import Toast from "../../components/Toast";

export default function AdminSessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const socketRef = useRef(null);

  const stats = useMemo(() => computeStats(sessions), [sessions]);

  // Connect socket once
  useEffect(() => {
    const token = localStorage.getItem("waec_token");
    const sessionToken = localStorage.getItem("waec_session");

    if (!token || !sessionToken) {
      setLoading(false);
      Toast.warn("Please login again.");
      return;
    }

    const socket = io(API_BASE_URL, {
      transports: ["websocket"],
      auth: { token, sessionToken },
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("admin:sessions:subscribe");
    });

    socket.on("admin:sessions:update", (data) => {
      setSessions(Array.isArray(data) ? data : []);
      setLoading(false);
    });

    socket.on("connect_error", (err) => {
      setLoading(false);
      Toast.error(err?.message || "Live session connection failed");
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const forceLogout = async (sessionId) => {
    const ok = window.confirm(
      "Force logout this user?\nThey will be logged out immediately."
    );
    if (!ok) return;

    try {
      await api.post(`/admin/sessions/${sessionId}/logout`);
      Toast.warn("Session terminated");
      // No manual refresh needed; server broadcasts update
    } catch {
      Toast.error("Failed to terminate session");
    }
  };

  if (loading) return <div style={{ padding: 20 }}>Loading live sessions…</div>;

  return (
    <div style={{ padding: 20 }}>
      <h2>🛡️ Active User Sessions (Live)</h2>

      {/* Analytics */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 16,
          margin: "20px 0",
        }}
      >
        <StatCard label="Total Active Sessions" value={stats.total} />
        <StatCard label="🟢 Active (<5 min)" value={stats.active} />
        <StatCard label="🟡 Idle (5–15 min)" value={stats.idle} />
        <StatCard label="🔴 Inactive (>15 min)" value={stats.inactive} />
        <StatCard label="⏱ Avg Idle Time (min)" value={stats.avgIdle} />
      </div>

      {/* Table */}
      {sessions.length === 0 ? (
        <p>No active sessions</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
            <thead>
              <tr>
                <Th>User</Th>
                <Th>IP Address</Th>
                <Th>Device</Th>
                <Th>Created</Th>
                <Th>Last Seen</Th>
                <Th>Status</Th>
                <Th>Action</Th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.id}>
                  <Td>
                    <strong>{s.full_name}</strong>
                    <br />
                    <small>{s.email}</small>
                  </Td>
                  <Td>{s.ip_address || "—"}</Td>
                  <Td title={s.user_agent}>{shortenAgent(s.user_agent)}</Td>
                  <Td>{formatTime(s.created_at)}</Td>
                  <Td>{formatTime(s.last_seen)}</Td>
                  <Td>{renderStatus(s.last_seen)}</Td>
                  <Td>
                    <button
                      onClick={() => forceLogout(s.id)}
                      style={{
                        background: "#d9534f",
                        color: "#fff",
                        border: "none",
                        padding: "6px 10px",
                        cursor: "pointer",
                        borderRadius: 4,
                      }}
                    >
                      Force Logout
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function computeStats(sessions) {
  const now = Date.now();
  let active = 0;
  let idle = 0;
  let inactive = 0;
  let totalIdleMinutes = 0;

  sessions.forEach((s) => {
    if (!s.last_seen) return;
   const mins =
  (Date.parse(new Date().toISOString()) -
   Date.parse(s.last_seen)) / 60000;

    totalIdleMinutes += mins;
    if (mins < 5) active++;
    else if (mins < 15) idle++;
    else inactive++;
  });

  return {
    total: sessions.length,
    active,
    idle,
    inactive,
    avgIdle: sessions.length ? (totalIdleMinutes / sessions.length).toFixed(1) : 0,
  };
}

function renderStatus(lastSeen) {
  if (!lastSeen) return "—";
  const mins = (Date.now() - new Date(lastSeen).getTime()) / 60000;
  if (mins < 5) return "🟢 Active";
  if (mins < 15) return "🟡 Idle";
  return "🔴 Inactive";
}

function formatTime(ts) {
  if (!ts) return "—";
  return new Date(ts).toLocaleString();
}

function shortenAgent(agent) {
  if (!agent) return "—";
  return agent.length > 45 ? agent.slice(0, 45) + "…" : agent;
}

function Th({ children }) {
  return (
    <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #ddd", whiteSpace: "nowrap" }}>
      {children}
    </th>
  );
}

function Td({ children }) {
  return (
    <td style={{ padding: 8, borderBottom: "1px solid #eee", verticalAlign: "top" }}>
      {children}
    </td>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={{ padding: 16, borderRadius: 8, background: "#f8f9fa", border: "1px solid #e0e0e0" }}>
      <div style={{ fontSize: 13, color: "#666" }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: "bold", marginTop: 6 }}>{value}</div>
    </div>
  );
}
